import { useState } from "react";
import { Box, Stack, Typography, Divider, Button, Snackbar, Alert, Chip, Tooltip } from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CancelIcon from "@mui/icons-material/Cancel";
import LanIcon from "@mui/icons-material/Lan";
import Network from "../components/HierarchyRatesOverrideComputation";
import HierarchyRearrangeDialog from "../components/UpdateHierarchyDialog";
import { useResponsive } from "../../../utils/mobileresponsive";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import useFetchNetwork from "../hooks/useFetchNetwork";
import { isFieldAgent } from "../../../utils/roleDetection";

const searchParams = new URLSearchParams(window.location.search);
const levelFilter = searchParams.get("level")?.toLowerCase() ?? null;

export default function NetworkShell({
	userId = null,
	currentAgent = null,
	headerSlot = null,
	footerSlot = null,
	updateHierarchy,
	updateHierarchyLoading,
}) {
	const stored = localStorage.getItem("loggedin_data");
	const user = stored ? JSON.parse(stored) : null;
	const roles = Array.isArray(user?.roles) ? user.roles : [];
	const isAgent = isFieldAgent(roles);
	const { isMobile } = useResponsive();
	const { network, loading, refetch } = useFetchNetwork(userId);
	const [rearrangeOpen, setRearrangeOpen] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

	const loggedInAgent = isAgent ? `${user?.name} ${user?.last_name}` : null;

	// Derive L1/L2/L3 override totals from real network data
	const overrideTotals = {
		l1_earnings: (network?.l1 ?? []).reduce((sum, a) => sum + (a.override_earning ?? 0), 0),
		l2_earnings: (network?.l2 ?? []).reduce((sum, a) => sum + (a.override_earning ?? 0), 0),
		l3_earnings: (network?.l3 ?? []).reduce((sum, a) => sum + (a.override_earning ?? 0), 0),
	};

	// Opted-out agents are no longer returned by name. The only ones left in the
	// tree are anonymised placeholders, kept solely so their opted-in downlines
	// still have a path to the root — this explains the unnamed greyed nodes.
	const placeholderNodes = ["l1", "l2", "l3"].reduce((sum, key) => sum + (network?.[key] ?? []).filter((a) => a.is_placeholder === true).length, 0);

	const showSnackbar = (message, severity = "info") => setSnackbar({ open: true, message, severity });
	const handleSnackbarClose = () => setSnackbar((prev) => ({ ...prev, open: false }));

	// Receives the net-diff `moves` array from HierarchyRearrangeDialog's computeMoves().
	const handleSaveHierarchy = async (moves) => {
		if (!updateHierarchy) {
			console.error("updateHierarchy handler was not provided to NetworkShell");
			showSnackbar("Unable to save changes right now. Please try again later.", "error");
			return;
		}

		try {
			const result = await updateHierarchy({ moves });

			if (!result?.success) {
				showSnackbar(result?.error || "Something went wrong while updating the hierarchy.", "error");
				return;
			}

			showSnackbar(result?.data?.message || "Hierarchy updated successfully.", "success");
			setRearrangeOpen(false);
			refetch?.();
		} catch (err) {
			console.error(err);
			showSnackbar("Unexpected error occurred while saving the hierarchy.", "error");
		}
	};

	return (
		<Box p={4}>
			{/* Header */}
			<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent="space-between" spacing={2} px={3}>
				<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
					{isAgent ? "MY NETWORK" : "AGENTS NETWORK"}
				</Typography>

				{isAgent ? (
					<Typography fontSize={14} color="text.secondary">
						Viewing as: <strong>{loggedInAgent}</strong>
					</Typography>
				) : (
					headerSlot
				)}
			</Stack>

			<Box my={3}>
				<Divider />
			</Box>

			{/* Totals + rearrange button */}
			<Stack direction={{ xs: "column", md: "row" }} alignItems="center !important" justifyContent="space-between" pl={1} pb={2.5} gap={1}>
				<Stack direction="row" alignItems="center !important" gap={5}>
					<Typography variant="h6" letterSpacing={1}>
						TOTALS:
					</Typography>
					<Stack direction="row" alignItems="center !important" gap={{ xs: 4, md: 10 }}>
						{[
							{ label: "L1", color: "#00ACC1", value: overrideTotals.l1_earnings },
							{ label: "L2", color: "#1976D2", value: overrideTotals.l2_earnings },
							{ label: "L3", color: "#7E57C2", value: overrideTotals.l3_earnings },
						].map(({ label, color, value }) => (
							<Stack key={label} direction="row" alignItems="center !important" gap={0.8}>
								<Typography variant="body2">{label}:</Typography>
								<Typography variant="body1" color={color} fontWeight={700}>
									${value.toFixed(2)}
								</Typography>
							</Stack>
						))}

						{placeholderNodes > 0 && (
							<Tooltip
								title={`${placeholderNodes} agent${
									placeholderNodes === 1 ? " in this network has" : "s in this network have"
								} opted out of the service and been removed from the portal. Their position is shown unnamed only so the opted-in agents beneath them keep their place in the tree, and they contribute nothing to these totals.`}
							>
								<Chip
									icon={<CancelIcon sx={{ fontSize: "13px !important", color: "#78909C !important" }} />}
									label={`${placeholderNodes} excluded`}
									size="small"
									sx={{
										height: 22,
										fontSize: 11,
										fontWeight: 700,
										bgcolor: "#ECEFF1",
										color: "#546E7A",
										"& .MuiChip-label": { px: 0.75 },
									}}
								/>
							</Tooltip>
						)}
					</Stack>
				</Stack>

				{!isAgent && (
					<Button
						startIcon={<LanIcon />}
						variant="contained"
						size="small"
						onClick={() => setRearrangeOpen(true)}
						sx={{
							px: 2.5,
							gap: 0.5,
							backgroundColor: "#1D54A2 !important",
							...generateButtonSx({ bg: "#EBF5FF !important", text: "#1D54A2" }, { bg: "#EBF5FF !important", text: "#1D54A2" }),
						}}
					>
						Update Agent Downline
					</Button>
				)}
			</Stack>

			{/* The root can no longer be an opted-out agent: they are excluded from the
			    agent picker and cannot log in, so there is no route to this page for
			    one. The banner that used to explain a $0 root has gone with it. */}

			{/* Network tree or empty state */}
			{currentAgent || isAgent ? (
				<Network currentAgent={isAgent ? user : currentAgent} levelFilter={levelFilter} network={network} loading={loading} />
			) : (
				<Stack
					alignItems="center !important"
					justifyContent="center"
					spacing={2}
					sx={{ mt: 6, py: 8, border: "1.5px dashed #CFD8DC", borderRadius: 3, background: "#FAFBFC" }}
				>
					<Box
						sx={{
							width: 64,
							height: 64,
							borderRadius: "50%",
							bgcolor: "#E0F7FA",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AccountTreeIcon sx={{ fontSize: 32, color: "#00ACC1" }} />
					</Box>
					<Box textAlign="center">
						<Typography fontWeight={700} fontSize={16} color="text.primary">
							No Agent Selected
						</Typography>
						<Typography fontSize={13} color="text.secondary" mt={0.5}>
							Select an agent from the dropdown above to view their network.
						</Typography>
					</Box>
				</Stack>
			)}

			<HierarchyRearrangeDialog
				open={rearrangeOpen}
				onClose={() => setRearrangeOpen(false)}
				currentAgent={currentAgent}
				network={network}
				onSave={handleSaveHierarchy}
				saving={updateHierarchyLoading}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleSnackbarClose}
					severity={snackbar.severity}
					sx={{ width: "100%", "& .MuiAlert-action button": { backgroundColor: "transparent" } }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>

			{footerSlot}
		</Box>
	);
}
